import React, { useCallback, useEffect, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { useExtractionResultSocketHook, useIsViewerPermission, useNavigationHooks } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions, useHideExtractorGuideSelector, useUserActions, useUserRoleSelector } from "store";
import { IDataExtractionTag, IFileRepo, IUpdateFileTagData } from "store/types";
import { toastify } from "utils/tyostify";

import { RoleName } from "../../constants";
import { Routes } from "../../constants/routes";
import { DataExtractionHeader } from "./components/DataExtractionHeader";
import { DataExtractionMetadata } from "./components/DataExtractionMetadata";
import { DataExtractionParametersTable } from "./components/DataExtractionParametersTable";
import { DataExtractionPdfViewer } from "./components/DataExtractionPdfViewer";
import { EditParameterPrompt } from "./components/EditParameterPrompt";
import { ExtractDataGuideDialog } from "./components/ExtractDataGuideDialog";
import { RerunExtractorDialog } from "./components/RerunExtractorDialog";
import { UpdateBar } from "./components/UpdateBar";
import { useExtractionParameters } from "./hooks/useExtractionParameters";

export type DataExtractionSource = "deal" | "globalWorkspace" | "fileRepository";

interface Props {
  fileIds: number[];
  fileNames?: Record<number, string>;
  initialShowParameters?: boolean;
  source?: DataExtractionSource;
  hideDoneButton?: boolean;
  onUpdateFile?: (fileId: number, updates: Partial<IFileRepo>) => void;
  onCloseWithFileId?: (fileId: number) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const READ_ONLY_ROLES: RoleName[] = [RoleName.Guest, RoleName.Viewer, RoleName.TeamViewer];

export const DataExtractionModal: React.FC<Props> = ({
  fileIds,
  fileNames = {},
  initialShowParameters = true,
  source,
  hideDoneButton = false,
  onUpdateFile,
  onCloseWithFileId,
  onSubmit,
  onCancel
}) => {
  const role = useUserRoleSelector();
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const isReadOnlyRole =
    isAnalystViewerPermission ||
    isAnalystDealsViewerPermission ||
    Boolean(role?.name && READ_ONLY_ROLES.includes(role.name as RoleName));

  const [currentIndex, setCurrentIndex] = useState(0);
  const [fileData, setFileData] = useState<IFileRepo | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);

  const currentFileId = fileIds[currentIndex];

  const { updateHideExtractorGuideAction } = useUserActions();
  const { getFileByIdAction, updateFileTagAction, saveFileTagsAction, getDealInvestors, reRunFileExtractionAction } =
    useDealActions();
  const { action: fetchFile, loading: fileLoading } = useActionHook(getFileByIdAction);
  const { pathname } = useLocation();
  const { setParams } = useNavigationHooks();
  const { action: saveTags, loading: saving } = useActionHook(saveFileTagsAction, {
    onSuccess: () => {
      setTagErrors({});
      toastify({ message: "Data saved successfully", type: "success" });
      if (matchPath(pathname, { path: Routes.dealMonitoringData, exact: true })) {
        setParams({ search: { refetchDealMonitoringData: true } });
      }
    }
  });

  const runAiExtractionFn = useCallback(
    (instructions?: string) => {
      if (currentFileId == null) return Promise.resolve();
      return reRunFileExtractionAction({ file_id: currentFileId, prompt: instructions ?? "" });
    },
    [reRunFileExtractionAction, currentFileId]
  );
  const { action: runAiExtraction, loading: extractorLoading } = useActionHook(runAiExtractionFn);

  const [tagErrors, setTagErrors] = useState<Record<number, string[]>>({});

  useEffect(() => {
    if (!currentFileId) return;
    let cancelled = false;
    setFileData(null);
    fetchFile(currentFileId).then((data) => {
      if (cancelled || !data) return;
      setFileData(data);
    });
    return () => {
      cancelled = true;
    };
  }, [currentFileId]);

  useExtractionResultSocketHook({
    fileId: currentFileId,
    onResult: (success) => {
      if (success) setHasUpdate(true);
    }
  });

  useEffect(() => {
    setMetadataErrors((prev) => {
      const next = { ...prev };
      if (fileData?.effective_date && next.effectiveDate) delete next.effectiveDate;
      if (fileData?.currency && next.currency) delete next.currency;
      if ((fileData?.investor || fileData?.all_investors) && next.investor) delete next.investor;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [fileData]);

  const {
    parameters,
    loading: parametersLoading,
    deleteParameter,
    updateTag,
    addTag,
    refetch: refetchParameters,
    silentRefetch: silentRefetchParameters
  } = useExtractionParameters(currentFileId);

  const hideExtractorGuideDialog = useHideExtractorGuideSelector();
  const readOnly = fileData ? !fileData.deal || isReadOnlyRole : false;
  const [showParameters, setShowParameters] = useState(initialShowParameters);
  const [hoveredPolygonId, setHoveredPolygonId] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(!initialShowParameters && !hideExtractorGuideDialog);
  const [showRerunDialog, setShowRerunDialog] = useState(false);
  const [metadataErrors, setMetadataErrors] = useState<{
    effectiveDate?: string;
    currency?: string;
    investor?: string;
  }>({});
  const [metadataFocusField, setMetadataFocusField] = useState<"effectiveDate" | "currency" | "investor" | null>(null);

  const [dealInvestors, setDealInvestors] = useState<{ id: number; name: string; is_splittable?: boolean }[]>([]);
  const { action: fetchDealInvestors, loading: investorsLoading } = useActionHook(() =>
    fileData?.deal?.id ? getDealInvestors({ dealId: fileData.deal.id, with_all_investors: true }) : Promise.resolve([])
  );

  useEffect(() => {
    if (!fileData?.deal?.id) {
      setDealInvestors([]);
      return;
    }
    fetchDealInvestors().then((data) => {
      if (Array.isArray(data)) {
        setDealInvestors(
          data.map((inv: { id: number; name: string; is_splittable?: boolean }) => ({
            id: inv.id,
            name: inv.name,
            is_splittable: inv.is_splittable
          }))
        );
      }
    });
  }, [fileData?.deal?.id]);

  const pdfBoxRef = useRef<HTMLDivElement>(null);
  const [pdfContainerWidth, setPdfContainerWidth] = useState(0);

  useEffect(() => {
    const el = pdfBoxRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setPdfContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleTagCreated = useCallback(
    (tag: IDataExtractionTag, isNew: boolean) => {
      if (isNew) {
        addTag(tag);
      } else {
        updateTag(tag);
        setTagErrors((prev) => {
          if (!(tag.id in prev)) return prev;
          const { [tag.id]: _, ...rest } = prev;
          return rest;
        });
      }
      setShowParameters(true);
    },
    [addTag, updateTag]
  );

  const handleUpdateTag = useCallback(
    async (tagId: number, updates: IUpdateFileTagData) => {
      setTagErrors((prev) => {
        if (!(tagId in prev)) return prev;
        const { [tagId]: _, ...rest } = prev;
        return rest;
      });
      const updatedTag = await updateFileTagAction(currentFileId, tagId, updates);
      if (updatedTag) {
        updateTag(updatedTag);
      }
    },
    [currentFileId, updateTag]
  );

  const handleFileChange = (index: number) => {
    setCurrentIndex(index);
  };

  const [editPopover, setEditPopover] = useState<{
    param: IDataExtractionTag;
    anchorEl: HTMLElement;
  } | null>(null);

  const handleEditParameter = useCallback((param: IDataExtractionTag, anchorEl: HTMLElement) => {
    setEditPopover({ param, anchorEl });
  }, []);

  const handleEditSubmit = useCallback(
    (updatedTag: IDataExtractionTag) => {
      updateTag(updatedTag);
      setTagErrors((prev) => {
        if (!(updatedTag.id in prev)) return prev;
        const { [updatedTag.id]: _, ...rest } = prev;
        return rest;
      });
      setEditPopover(null);
    },
    [updateTag]
  );

  useEffect(() => {
    setHasUpdate(false);
  }, [currentFileId]);

  if (!currentFileId) return null;

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100vh",
        bgcolor: "#f6f8fc",
        padding: "16px 0",
        gap: "16px",
        outline: "none",
        overflow: "hidden"
      }}
    >
      <DataExtractionHeader
        fileIds={fileIds}
        fileNames={fileNames}
        currentIndex={currentIndex}
        source={source}
        dealId={fileData?.deal?.id}
        dealName={fileData?.deal?.title}
        sectionId={fileData?.section_id}
        sectionTitle={fileData?.section_title}
        fileName={fileData?.name || fileNames[currentFileId]}
        showExtractData={showParameters}
        readOnly={readOnly}
        onToggleMode={() => {
          setShowParameters((prev) => {
            if (!prev && !hideExtractorGuideDialog) setShowGuide(true);
            return !prev;
          });
        }}
        onPrev={() => handleFileChange(currentIndex - 1)}
        onNext={() => handleFileChange(currentIndex + 1)}
        onSelectFile={handleFileChange}
        onClose={() => {
          onCloseWithFileId?.(currentFileId);
          onCancel();
        }}
      />
      {hasUpdate && (
        <UpdateBar
          onClose={() => setHasUpdate(false)}
          onRefresh={async () => {
            setHasUpdate(false);
            const [data] = await Promise.all([fetchFile(currentFileId), silentRefetchParameters()]);
            if (data) setFileData(data);
          }}
        />
      )}

      <DataExtractionMetadata
        fileData={fileData}
        loading={fileLoading}
        readOnly={readOnly}
        showExtractData={showParameters}
        extractorLoading={extractorLoading}
        onRerunExtractor={() => setShowRerunDialog(true)}
        onFileUpdated={(updatedFile) => {
          setFileData((prev) => (prev ? { ...prev, ...updatedFile, file_url: prev.file_url } : updatedFile));
          onUpdateFile?.(currentFileId, updatedFile);
        }}
      />

      <Stack direction="row" sx={{ flex: 1, overflow: "hidden", gap: "16px", px: "16px" }}>
        {!readOnly && showParameters && (
          <Stack
            sx={{
              flex: 4,
              minWidth: 0,
              overflow: "auto",
              bgcolor: "white",
              borderRadius: "8px",
              boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
              p: "16px"
            }}
          >
            {parametersLoading ? (
              <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Stack>
            ) : (
              <DataExtractionParametersTable
                parameters={parameters}
                tagErrors={tagErrors}
                hoveredPolygonId={hoveredPolygonId}
                onHoverPolygon={setHoveredPolygonId}
                onUpdate={handleUpdateTag}
                onDelete={(tagId) => {
                  deleteParameter(tagId);
                  setTagErrors((prev) => {
                    if (!(tagId in prev)) return prev;
                    const { [tagId]: _, ...rest } = prev;
                    return rest;
                  });
                }}
                onEdit={handleEditParameter}
                fileData={fileData}
                readOnly={readOnly}
                dealInvestors={dealInvestors}
                investorsLoading={investorsLoading}
                onFileUpdated={(updatedFile) => {
                  setFileData((prev) => (prev ? { ...prev, ...updatedFile, file_url: prev.file_url } : updatedFile));
                  silentRefetchParameters();
                  onUpdateFile?.(currentFileId, updatedFile);
                }}
                metadataErrors={metadataErrors}
                focusField={metadataFocusField}
              />
            )}
          </Stack>
        )}
        <Box
          ref={pdfBoxRef}
          sx={{
            ...(showParameters ? { flex: 6, minWidth: 0 } : { width: "900px", maxWidth: "100%", mx: "auto" }),
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "white",
            borderRadius: "8px",
            boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)"
          }}
        >
          <DataExtractionPdfViewer
            fileId={currentFileId}
            fileUrl={fileData?.file_url || null}
            fileName={fileData?.name || ""}
            extractionTags={readOnly ? [] : parameters}
            onTagCreated={handleTagCreated}
            containerWidth={pdfContainerWidth}
            readOnly={readOnly}
            showPolygons={showParameters && !readOnly}
            hoveredPolygonId={hoveredPolygonId}
            onHoverPolygon={setHoveredPolygonId}
          />
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="8px" sx={{ px: "48px" }}>
        <Button
          onClick={() => {
            onCloseWithFileId?.(currentFileId);
            onCancel();
          }}
          sx={{
            borderRadius: "32px",
            border: "1px solid #7b92a9",
            color: "#071f35",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px"
          }}
        >
          Close
        </Button>
        {!readOnly && showParameters && !hideDoneButton && (
          <span>
            <Button
              disabled={saving}
              onClick={async () => {
                if (parameters.length) {
                  const requiresInvestor = parameters.some((p) => p.measure && !p.measure.is_global_value);
                  const errors: { effectiveDate?: string; currency?: string; investor?: string } = {};

                  if (!fileData?.effective_date) errors.effectiveDate = "Effective date is required";
                  if (!fileData?.currency) errors.currency = "Currency is required";
                  if (
                    requiresInvestor &&
                    !fileData?.investor &&
                    !fileData?.all_investors &&
                    !fileData?.investor_ids?.length
                  ) {
                    errors.investor = "Investor is required";
                  }

                  if (Object.keys(errors).length) {
                    setMetadataErrors(errors);
                    const firstField = errors.effectiveDate
                      ? "effectiveDate"
                      : errors.currency
                        ? "currency"
                        : "investor";
                    setMetadataFocusField((prev) => (prev === firstField ? null : firstField));
                    setTimeout(() => setMetadataFocusField(firstField), 0);
                    return;
                  }
                  setMetadataErrors({});
                }

                try {
                  await saveTags(currentFileId);
                  onCloseWithFileId?.(currentFileId);
                  onSubmit();
                } catch (err) {
                  if (err && typeof err === "object" && "errors" in err) {
                    const errors = (err as { errors: Record<string, string[]> }).errors;
                    const parsed: Record<number, string[]> = {};
                    for (const [key, messages] of Object.entries(errors)) {
                      const id = Number(key);
                      if (!isNaN(id)) parsed[id] = messages;
                    }
                    setTagErrors(parsed);
                  }
                }
              }}
              sx={{
                borderRadius: "32px",
                bgcolor: "#071f35",
                color: "white",
                textTransform: "none",
                padding: "4px 16px",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                "&:hover": { bgcolor: "#0a2a45" },
                "&:disabled": { bgcolor: "#bac7ce", color: "white" }
              }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Done"}
            </Button>
          </span>
        )}
      </Stack>
      <ExtractDataGuideDialog
        open={showGuide && showParameters && !readOnly}
        onClose={() => {
          setShowGuide(false);
          updateHideExtractorGuideAction();
        }}
      />
      {editPopover && (
        <EditParameterPrompt
          fileId={currentFileId}
          dealId={fileData?.deal?.id ?? null}
          dealInvestors={dealInvestors}
          parameter={editPopover.param}
          anchorEl={editPopover.anchorEl}
          onSubmit={handleEditSubmit}
          onCancel={() => setEditPopover(null)}
        />
      )}
      <RerunExtractorDialog
        open={showRerunDialog}
        loading={extractorLoading}
        onClose={() => setShowRerunDialog(false)}
        onRerun={(instructions) => {
          runAiExtraction(instructions).then(() => {
            setShowRerunDialog(false);
            refetchParameters();
          });
        }}
      />
    </Stack>
  );
};
